import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSessao, clearSessao } from '../../lib/auth';
import { categoriaPermitida, temAcessoPagina } from '../../lib/permissoes';
import AvisoAtividades from '../AvisoAtividades';
import { useParaDecidirComunidade } from '../../lib/api/comunidade';
import { api } from '../../lib/api/client';
import { centralPageFor, centralPath } from '../../pages/CentralOrdens/paths';
import styles from './Shell.module.css';
import logoUrl from '../../../logo-preta.png';

type NavItem = {
  page: string;
  label: ReactNode;
  description?: string;
  section?: string;
  hidden?: boolean;
  href?: string; // navegação full-page (para páginas HTML fora do React)
  adminOnly?: boolean;
};

type Category = {
  id: string;
  label: string;
  short?: string; // nome curto na barra (o completo aparece no topo do menu)
  direct?: boolean; // botão que abre direto o 1º item (sem dropdown)
  destaque?: boolean; // botão verde (Início)
  items: NavItem[];
};

const CATEGORIES: Category[] = [
  // Menu (29/10/2026): Início › Conteúdo › Marketing › Comercial › Atendimento › Planos › Pedagógico › Discursivas ›
  // Financeiro › Admin. O Início reúne o painel, o calendário geral, a gestão de atividades, a gestão de pessoas e os
  // links. Painel de tarefas, Projetos, Reuniões, Equipe e Nova ordem saíram do menu (as rotas continuam).
  {
    id: 'inicio',
    label: 'Início',
    destaque: true,
    items: [
      { page: 'home', label: 'Painel' },
      { page: 'assistente', label: 'Assistente (IA)' },
      { page: 'calendario-acoes', label: 'Calendário geral' },
      { page: 'atividades', label: 'Gestão de atividades' },
      { page: 'central-ordens-pessoas', label: 'Gestão de pessoas' },
      { page: 'links', label: 'Links úteis' },
    ],
  },
  {
    id: 'conteudo',
    label: 'Conteúdo',
    items: [
      { page: 'conteudo-calendario', label: 'Calendário de conteúdo' },
      { page: 'conteudo-especialistas', label: 'Especialistas (IA)' },
      { page: 'conteudo-direct', label: 'Direct' },
      { page: 'conteudo-prompt-social-seller', label: 'Prompt Social Seller' },
      { page: 'conteudo-social-seller', label: 'Lista Social Seller' },
      { page: 'conteudo-debriefing', label: 'Debriefing' },
    ],
  },
  // Marketing (28/10/2026): Atividades do dia (checklist), Calendário de marketing, Funis (cada funil com estrutura, calculadora, calendário,
  // mensagens de grupo e API, e-mails e métricas), Gestão de e-mails e Tráfego.
  {
    id: 'marketing',
    label: 'Marketing',
    items: [
      { page: 'marketing-atividades', label: 'Atividades do dia (atalho)' },
      { page: 'calendario-lancamentos', label: 'Calendário de marketing' },
      { page: 'marketing-funis', label: 'Funis' },
      { page: 'marketing-grupos', label: 'Grupos ao vivo' },
      { page: 'marketing-email', label: 'Gestão de e-mails' },
      { page: 'marketing-trafego', label: 'Tráfego' },
    ],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    items: [
      { page: 'comercial-dashboard', label: 'Dashboard' },
      { page: 'comercial-vendas', label: 'Lista de Vendas' },
      { page: 'comercial-relatorio', label: 'Relatório Diário' },
      { page: 'leads-esteira', label: 'Esteira de Leads' },
      { page: 'comercial-config', label: 'Configurações' },
      { page: 'bonus', label: 'Bônus' },
      { page: 'negociacao', label: 'Ofertas e negociação' },
      { page: 'embaixadores', label: 'Indicações' },
    ],
  },
  {
    id: 'atendimento',
    label: 'Atendimento ao Aluno',
    short: 'Atendimento',
    items: [
      // ordem pedida em 24/10/2026. Alunos Recorrentes, a Vencer e sem Estudar viraram abas da página "Alunos";
      { page: 'atendimento-painel', label: 'Painel' },
      { page: 'pessoas', label: 'Central de alunos' },
      { page: 'conversas', label: 'Suporte' },
      { page: 'atendimento-comunidade', label: 'Chat da comunidade' },
      { page: 'alunos-gestao', label: 'Alunos' },
      { page: 'bonus', label: 'Lista de Bônus' },
      { page: 'corrigir-email', label: 'Corrigir Email' },
      { page: 'migracao-tutory', label: 'Migração Tutory' },
      { page: 'respostas-formulario', label: 'Respostas ao Formulário' },
      { page: 'processo', label: 'Processo de entrada' },
      { page: 'alunos-atrasados', label: 'Alunos Recorrentes', hidden: true },
      { page: 'alunos-a-vencer', label: 'Alunos a Vencer', hidden: true },
      { page: 'alunos-sem-estudar', label: 'Alunos sem Estudar', hidden: true },
      { page: 'aprovados', label: 'Aprovados' },
      { page: 'problemas-portal', label: 'Problemas do Portal', hidden: true },
    ],
  },
  // Operação diária: fica perto de Atendimento, antes das áreas administrativas.
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    items: [
      { page: 'central-whatsapp', label: 'Caixa de entrada', description: 'Conversas, filas e atendimento da equipe', section: 'Dia a dia' },
      { page: 'whatsapp-disparos', label: 'Disparos', description: 'Campanhas por template e histórico', section: 'Dia a dia' },
      { page: 'whatsapp-conhecimento', label: 'Base de conhecimento', description: 'Conteúdo usado nas respostas do agente', section: 'Agente' },
      // Teste da equipe: só fora de produção (o servidor também exige WA_SIMULADOR_HABILITADO=1).
      ...(__VERCEL_ENV__ !== 'production' ? [{ page: 'whatsapp-simulador', label: 'Simulador', description: 'Teste conversas sem enviar mensagens reais', section: 'Agente' }] : []),
      { page: 'whatsapp-config', label: 'Configurar agente', description: 'Autonomia, horários, limites e ações', section: 'Administração' },
      { page: 'whatsapp-gerenciador', label: 'Conta Meta', description: 'Números, templates, webhook e custos', section: 'Administração' },
    ],
  },
  {
    id: 'planos-estudo',
    label: 'Planos de Estudo',
    short: 'Planos',
    items: [
      { page: 'admin-metas', label: 'Matriz de Assuntos' },
      { page: 'admin-config-plano', label: 'Configuração dos Planos' },
      { page: 'admin-alunos-config', label: 'Personalização dos Alunos' },
      { page: 'admin-aulas-apoio', label: 'Aulas de Apoio' },
      { page: 'admin-avisos-plano', label: 'Avisos do Plano' },
      { page: 'admin-simulados-cronograma', label: 'Simulados no Cronograma' },
      { page: 'admin-cadernos', label: 'Cadernos de Questões (Admin)', hidden: true },
      { page: 'admin-cronograma-planos', label: 'Cronograma Planos (Admin)', hidden: true },
    ],
  },
  {
    id: 'pedagogico',
    label: 'Pedagógico',
    items: [
      // ordem pedida em 24/10/2026; "Problemas do Portal" saiu de todos os menus (não está sendo usado)
      { page: 'erros-reportados', label: 'Gestão dos erros' },
      { page: 'avaliacoes-conteudo', label: 'Avaliação das aulas' },
      { page: 'admin-simulados', label: 'Simulados' },
      { page: 'encontros', label: 'Aulas ao vivo' },
      { page: 'admin-flashcards', label: 'Flashcards' },
      { page: 'cadernos-questoes', label: 'Cadernos de questões' },
      { page: 'banco-questoes-painel', label: 'Banco de Questões' },
      { page: 'banco-questoes-lista', label: 'Banco de Questões › Questões', hidden: true },
      { page: 'banco-questoes-revisao', label: 'Banco de Questões › Revisar por humano', hidden: true },
      { page: 'banco-questoes-recursos', label: 'Banco de Questões › Recursos', hidden: true },
      { page: 'banco-questoes-comentarios', label: 'Banco de Questões › Comentários', hidden: true },
      { page: 'banco-questoes-acessos', label: 'Banco de Questões › Acessos avulsos', hidden: true },
      { page: 'banco-questoes-ipq', label: 'Banco de Questões › IP Questões (vendas)', hidden: true },
      { page: 'banco-questoes-por-meta', label: 'Banco de Questões › Questões por meta', hidden: true },
      { page: 'admin-comece-aqui', label: 'Comece aqui (Portal do aluno)' },
      { page: 'corrigir-email', label: 'Corrigir Email' },
      { page: 'migracao-tutory', label: 'Migração Tutory' },
      { page: 'problemas-portal', label: 'Problemas do Portal', hidden: true },
    ],
  },
  {
    id: 'discursivas',
    label: 'Discursivas',
    items: [
      { page: 'admin-discursivas-correcao', label: 'Lista de correção' },
      { page: 'admin-discursivas', label: 'Temas' },
      { page: 'lista-discursivas', label: 'Lista de Discursivas', hidden: true },
    ],
  },
  // Conteúdo: Direct (conversas do Instagram com o Social Seller IA, alertas e agente), cadastro do Direct
  // do Social Seller humano (a gestão fica em Comercial › Indicações) e Debriefing (painel do Instagram).
  {
    id: 'financeiro',
    label: 'Financeiro',
    items: [
      { page: 'financeiro', label: 'Visão geral' },
      { page: 'financeiro-mes', label: 'Mês' },
      { page: 'financeiro-despesas', label: 'Despesas' },
      { page: 'financeiro-regras', label: 'Regras' },
    ],
  },
  {
    id: 'admin',
    label: 'Administração',
    short: 'Admin',
    items: [
      { page: 'admin-usuarios', label: 'Usuários' },
      { page: 'admin-perfis', label: 'Perfis' },
      { page: 'admin-logs', label: 'Logs de Acesso' },
      { page: 'migracao-tutory', label: 'Migração Tutory' },
      { page: 'admin-corretores', label: 'Corretores' },
      { page: 'problemas-portal', label: 'Problemas do Portal', hidden: true },
    ],
  },
  // Oculto apenas em produção Vercel. Aparece em dev local e preview.
  ...(__VERCEL_ENV__ !== 'production' ? [{
    id: 'homologacao',
    label: 'Homologação',
    items: [
      { page: 'homologacao', label: 'Central de Homologação' },
    ],
  }] : []),
];

type DropdownPos = { top: number; left: number; right?: number };

export function Shell({ children }: { children: ReactNode }) {
  const sessao = getSessao();
  // WhatsApp: conversas com você esperando a sua resposta (link direto se for uma só).
  const [waSuaVez, setWaSuaVez] = useState<{ total: number; conversa_id: string | null }>({ total: 0, conversa_id: null });
  const navigate = useNavigate();
  const location = useLocation();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({ top: 0, left: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Gestão da equipe vive em /ordens/*: destaca o item do menu correspondente
  const currentPage = centralPageFor(location.pathname) ?? (location.pathname.replace('/', '') || 'home');

  const closeDropdown = useCallback(() => setOpenCategory(null), []);

  // Fecha o menu mobile ao trocar de rota
  useEffect(() => {
    if (!sessao) return;
    let vivo = true;
    const buscarWa = () => api.get<{ total: number; conversa_id: string | null }>('/api/wa/central/sua-vez')
      .then((r) => { if (vivo) setWaSuaVez(r); })
      .catch(() => { /* sem acesso à Central WhatsApp: sem aviso */ });
    void buscarWa();
    const tWa = setInterval(buscarWa, 30000);
    return () => { vivo = false; clearInterval(tWa); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Trava o scroll do body enquanto o drawer mobile está aberto
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Element;
      if (target.closest('[data-category-header]')) return;
      if (target.closest('[data-dropdown]')) return;
      closeDropdown();
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [closeDropdown]);

  function handleCategoryClick(id: string, e: React.MouseEvent<HTMLButtonElement>) {
    if (openCategory === id) {
      closeDropdown();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const pos: DropdownPos = { top: rect.bottom + 4, left: rect.left };
    setDropdownPos(pos);
    setOpenCategory(id);

    // Ajusta se sair da viewport pela direita (via rAF, depois do render)
    requestAnimationFrame(() => {
      if (dropdownRef.current) {
        const dr = dropdownRef.current.getBoundingClientRect();
        if (dr.right > window.innerWidth - 8) {
          setDropdownPos({ top: rect.bottom + 4, left: rect.right - dr.width });
        }
      }
    });
  }

  function handleNav(page: string, href?: string) {
    closeDropdown();
    if (href) { window.location.href = href; return; }
    navigate(page === 'home' ? '/' : centralPath(page) ?? `/${page}`);
  }

  function handleLogout() {
    clearSessao();
    window.location.reload();
  }

  const userInitial = sessao?.nome?.charAt(0).toUpperCase() ?? 'U';

  // ── Permissões por categoria (mesmas regras do legado, em lib/permissoes) ────
  // perfil admin (is_admin) conta como categoria admin
  const categorias = [...(sessao?.categorias ?? []), ...(sessao?.is_admin ? ['admin'] : [])];
  const ehCorretor = categorias.length === 1 && categorias[0] === 'corretor';
  // Quem vê cada aba: configurado pelo admin em Perfis (padrão em lib/permissoes; Financeiro só admin)
  const categoriasVisiveis = CATEGORIES.filter((c) => categoriaPermitida(c.id, categorias));

  // Barra: o que não couber na largura vai para "Mais ▾" (medido de verdade, recalcula ao redimensionar)
  const navRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(categoriasVisiveis.length);
  // Contadores no menu (bolinha vermelha): Chat da comunidade → mensagens ocultas que a equipe ainda não decidiu.
  // Consulta leve a cada 60 s, só para quem vê o item.
  const paraDecidirComunidade = useParaDecidirComunidade(!!sessao && temAcessoPagina('atendimento-comunidade', categorias));
  const catKey = `${categoriasVisiveis.map((c) => c.id).join(',')}|${paraDecidirComunidade ?? 0}|${waSuaVez.total}`; // o contador muda a largura
  useEffect(() => {
    function fit() {
      const nav = navRef.current, m = measureRef.current;
      if (!nav || !m) return;
      const widths = Array.from(m.children).map((el) => (el as HTMLElement).offsetWidth + 4);
      const [mais = 0, ...cats] = widths;
      let room = nav.clientWidth - 8;
      let n = 0;
      for (let i = 0; i < cats.length; i++) {
        const needMore = i < cats.length - 1 ? mais : 0;
        if (cats[i] + needMore > room && !(i === cats.length - 1 && cats[i] <= room)) break;
        room -= cats[i]; n++;
      }
      setVisibleCount(n);
    }
    fit();
    const ro = new ResizeObserver(fit);
    if (navRef.current) ro.observe(navRef.current);
    return () => ro.disconnect();
  }, [catKey]);
  const barCats = categoriasVisiveis.slice(0, visibleCount);
  const moreCats = categoriasVisiveis.slice(visibleCount);

  const openCat = categoriasVisiveis.find((c) => c.id === openCategory);
  const itemsOf = (cat: Category) => cat.items.filter((item) => !item.hidden && (!item.adminOnly || sessao?.is_admin) && temAcessoPagina(item.page, categorias));

  // Contadores no menu (definidos acima, com o hook)
  const contadores: Record<string, number> = { 'atendimento-comunidade': paraDecidirComunidade ?? 0, 'central-whatsapp': waSuaVez.total };
  const contaItem = (page: string) => contadores[page] ?? 0;
  const contaCat = (cat: Category) => itemsOf(cat).reduce((t, i) => t + contaItem(i.page), 0);
  const bolinha = (n: number, titulo?: string) => (n > 0 ? <span className={styles.contador} title={titulo}>{n > 99 ? '99+' : n}</span> : null);

  return (
    <div className={styles.root}>
      <header className={styles.topbar}>
        <div className={styles.logoSection}>
          <div className={styles.logo}>
            <img src={logoUrl} alt="Irmãos Polícia" />
          </div>
        </div>

        <nav className={styles.navMenu}>
          {/* medição invisível dos botões (para decidir o que cabe na barra) */}
          <div ref={measureRef} className={styles.navMeasure} aria-hidden>
            <span className={styles.categoryHeader}>Mais <span className={styles.categoryArrow}>▼</span></span>
            {categoriasVisiveis.map((cat) => <span key={cat.id} className={styles.categoryHeader}>{cat.short ?? cat.label}{bolinha(contaCat(cat))} <span className={styles.categoryArrow}>▼</span></span>)}
          </div>
          <div ref={navRef} className={styles.navLinks}>

            {barCats.map((cat) => {
              if (cat.direct && cat.items[0]) {
                const item = cat.items[0];
                return (
                  <button
                    key={cat.id}
                    className={`${styles.categoryHeader} ${currentPage === item.page ? styles.open : ''}`}
                    onClick={() => handleNav(item.page, item.href)}
                  >
                    {cat.short ?? cat.label}
                  </button>
                );
              }
              const isOpen = openCategory === cat.id;
              const here = itemsOf(cat).some((i) => i.page === currentPage);
              return (
                <button
                  key={cat.id}
                  data-category-header
                  title={cat.label}
                  className={`${styles.categoryHeader} ${isOpen || here ? styles.open : ''}`}
                  style={cat.destaque ? { background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' } : undefined}
                  onClick={(e) => handleCategoryClick(cat.id, e)}
                >
                  {cat.short ?? cat.label}
                  {bolinha(contaCat(cat), 'Itens esperando a equipe')}
                  <span className={styles.categoryArrow}>▼</span>
                </button>
              );
            })}
            {moreCats.length > 0 && (
              <button
                data-category-header
                className={`${styles.categoryHeader} ${openCategory === '__mais' ? styles.open : ''}`}
                onClick={(e) => handleCategoryClick('__mais', e)}
              >
                Mais
                {bolinha(moreCats.reduce((t, cat) => t + contaCat(cat), 0))}
                <span className={styles.categoryArrow}>▼</span>
              </button>
            )}
          </div>
        </nav>

        {waSuaVez.total > 0 && !location.pathname.startsWith('/central-whatsapp') && (
          <button type="button" className={styles.avisoWa}
            title="Conversas do WhatsApp esperando a sua resposta"
            onClick={() => navigate(waSuaVez.conversa_id ? `/central-whatsapp?conversa=${waSuaVez.conversa_id}` : '/central-whatsapp?fila=meus')}>
            <span className={styles.avisoWaPonto} />
            WhatsApp: {waSuaVez.total} esperando você
          </button>
        )}

        {sessao && (
          <div className={styles.userInfo}>
            <div className={styles.userRow}>
              <div className={styles.userAvatar}>{userInitial}</div>
              <div className={styles.userDetails}>
                <div className={styles.userName}>{sessao.nome}</div>
                <div className={styles.userPerfil}>{sessao.perfil_nome}</div>
              </div>
            </div>
            <button className={styles.logoutBtn} onClick={handleLogout}>
              Sair
            </button>
          </div>
        )}

        {/* Botão hambúrguer — só aparece no mobile (via CSS) */}
        <button
          className={styles.hamburger}
          aria-label="Abrir menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span className={mobileOpen ? styles.hamburgerOpen : ''} />
          <span className={mobileOpen ? styles.hamburgerOpen : ''} />
          <span className={mobileOpen ? styles.hamburgerOpen : ''} />
        </button>
      </header>

      {openCategory === '__mais' && moreCats.length > 0 && (
        <div ref={dropdownRef} data-dropdown className={`${styles.dropdown} ${styles.dropdownMore}`} style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {moreCats.map((cat) => (
            <div key={cat.id} className={styles.dropdownGroup}>
              <div className={styles.dropdownTitle}>{cat.label}</div>
              {itemsOf(cat).map((item) => (
                <button key={item.page} className={`${styles.navButton} ${currentPage === item.page ? styles.active : ''}`} onClick={() => handleNav(item.page, item.href)}>
                  {item.label}{bolinha(contaItem(item.page))}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {openCat && (
        <div
          ref={dropdownRef}
          data-dropdown
          className={`${styles.dropdown} ${openCat.id === 'whatsapp' ? styles.dropdownWhatsapp : ''}`}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <div className={styles.dropdownTitle}>{openCat.id === 'whatsapp' ? 'Operação WhatsApp' : openCat.label}</div>
          {openCat.id === 'whatsapp' && <div className={styles.dropdownSubtitle}>Atenda, automatize e acompanhe tudo em um só lugar.</div>}
          {itemsOf(openCat).map((item, index, itens) => (
            <div key={item.page} className={styles.dropdownItemWrap}>
              {item.section && item.section !== itens[index - 1]?.section && <div className={styles.dropdownSection}>{item.section}</div>}
              <button
                className={`${styles.navButton} ${currentPage === item.page ? styles.active : ''}`}
                onClick={() => handleNav(item.page, item.href)}
              >
                <span className={styles.dropdownItemCopy}>
                  <span>{item.label}{bolinha(contaItem(item.page))}</span>
                  {item.description && <small>{item.description}</small>}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drawer mobile — navegação completa em telas estreitas */}
      {mobileOpen && (
        <>
          <div className={styles.mobileOverlay} onClick={() => setMobileOpen(false)} />
          <nav className={styles.mobileDrawer} aria-label="Menu principal">
            {sessao && (
              <div className={styles.mobileUser}>
                <div className={styles.userAvatar}>{userInitial}</div>
                <div className={styles.userDetails}>
                  <div className={styles.userName}>{sessao.nome}</div>
                  <div className={styles.userPerfil}>{sessao.perfil_nome}</div>
                </div>
              </div>
            )}


            {categoriasVisiveis.map((cat) => (
              <div key={cat.id} className={styles.mobileSection}>
                <div className={styles.mobileSectionTitle}>{cat.label}</div>
                {cat.items.filter((item) => !item.hidden && (!item.adminOnly || sessao?.is_admin) && temAcessoPagina(item.page, categorias)).map((item) => (
                  <button
                    key={item.page}
                    className={`${styles.mobileLink} ${currentPage === item.page ? styles.active : ''}`}
                    onClick={() => handleNav(item.page, item.href)}
                  >
                    {item.label}{bolinha(contaItem(item.page))}
                  </button>
                ))}
              </div>
            ))}

            {sessao && (
              <button className={styles.mobileLogout} onClick={handleLogout}>
                Sair
              </button>
            )}
          </nav>
        </>
      )}

      <main className={styles.mainContent}>{sessao && !ehCorretor && <AvisoAtividades />}{children}</main>
    </div>
  );
}
